import Axios, { isAxiosError } from 'axios';
import { QueryClient } from '@tanstack/react-query';
import type { PublicPackage } from './types';

export const PUBLIC_PACKAGE_QUERY_KEY = 'publicPackage';

type RecaptchaEnterprise = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: { enterprise: RecaptchaEnterprise };
  }
}

let recaptcha: Promise<RecaptchaEnterprise> | undefined;

const loadRecaptcha = (siteKey: string) => {
  recaptcha ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => {
      const enterprise = window.grecaptcha?.enterprise;
      if (enterprise) enterprise.ready(() => resolve(enterprise));
      else reject(new Error('reCAPTCHA Enterprise is unavailable'));
    };
    script.onerror = () => {
      script.remove();
      recaptcha = undefined;
      reject(new Error('reCAPTCHA Enterprise failed to load'));
    };
    document.head.appendChild(script);
  });
  return recaptcha;
};

export const botToken = async (siteKey: string) =>
  (await loadRecaptcha(siteKey)).execute(siteKey, { action: 'package_identity' });

export const createPublicPackageClient = (baseURL: string) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  });
  const api = Axios.create({ baseURL, withCredentials: true });
  api.interceptors.response.use(undefined, (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 401)
      queryClient.setQueriesData<PublicPackage>({ queryKey: [PUBLIC_PACKAGE_QUERY_KEY] }, (pack) =>
        pack ? { ...pack, guestVerified: false } : pack,
      );
    return Promise.reject(error);
  });
  return { api, queryClient };
};
